import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { deltaText, refreshedClaim, sectionTitle } from '../../stores/slices/reviewsSlice';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import type { ReviewSection } from '../../types';
import ClaimValue from './ClaimValue';
import { WhyChip, openSectionDetails } from './ReviewSectionRow';
import { staleTile, tileSeries } from './reviewPage';

/** A tile's history as a line: its refreshes, oldest first, the last point marked. */
export function MiniSpark({ values, color }: { values: number[]; color: string }) {
  if (values.length < 2) return null;
  const W = 120, H = 30;
  const min = Math.min(...values), max = Math.max(...values), range = (max - min) || 1;
  const x = (i: number) => 2 + (i * (W - 6)) / (values.length - 1);
  const y = (v: number) => 3 + ((max - v) * (H - 7)) / range;
  const last = values.length - 1;
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true" data-testid="review-tile-spark"
      sx={{ display: 'block', width: '100%', height: 30, mt: 0.75 }}>
      <polyline points={values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} fill="none" stroke={color} strokeWidth="1.8" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      <circle cx={x(last)} cy={y(values[last])} r="2.6" fill={color} />
    </Box>
  );
}

/**
 * One KPI tile on the review (#568): the label (its details open in the side
 * panel), the number (a claim — Why, Pin, Annotate from the drawer), its move
 * since the last refresh, a line of its history, the ⚡ where the walk flagged
 * it, the deck check's stale flag with the real value, and its notes count.
 */
export default function ReviewTile({ threadId, section: s, reviewTitle, groups, notes }: {
  threadId: string; section: ReviewSection; reviewTitle: string; groups: string[]; notes: number;
}) {
  const thread = useVoyagerStore((st) => st.savedThreads.find((t) => t.id === threadId));
  const setGroup = useVoyagerStore((st) => st.setReviewSectionGroup);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const stale = staleTile(thread, s);
  const flagged = stale !== null;
  const up = (s.delta || 0) >= 0;
  const color = flagged ? 'var(--bad)' : s.refreshed_at && s.previous != null ? (up ? 'var(--ok)' : 'var(--bad)') : 'var(--ink-3)';
  const current = s.group && groups.includes(s.group) ? s.group : groups[0];
  return (
    <Box data-testid="review-tile" data-flag={flagged ? 'stale' : s.flag || undefined} sx={{
      position: 'relative', p: 1.5, pb: 1.25, borderRadius: 3.5, border: '1px solid', display: 'grid', alignContent: 'start',
      borderColor: flagged ? 'color-mix(in srgb, var(--bad) 45%, var(--line))' : s.flag ? 'var(--warn)' : 'var(--line)',
      background: 'linear-gradient(180deg, var(--surface), var(--surface2))', boxShadow: 'var(--shadow)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography role="button" tabIndex={0} data-testid="review-tile-open" title="Where this came from, its analyses, its commentary"
          onClick={() => openSectionDetails(threadId, s, reviewTitle)} onKeyDown={(e) => { if (e.key === 'Enter') openSectionDetails(threadId, s, reviewTitle); }}
          sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', cursor: 'pointer', '&:hover': { color: 'var(--accent-ink)' } }} noWrap>
          {sectionTitle(s)}
        </Typography>
        {notes > 0 && (
          <Box component="span" data-testid="review-tile-notes" sx={{ fontSize: 11, color: 'var(--accent-ink)', bgcolor: 'var(--accent-soft)', borderRadius: 999, px: 0.9 }}>
            {notes} note{notes === 1 ? '' : 's'}
          </Box>
        )}
        {groups.length > 1 && (
          <IconButton size="small" aria-label={`Move ${sectionTitle(s)}`} data-testid="review-tile-move" onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.25, color: 'text.disabled' }}>
            <MoreHorizIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <ClaimValue claim={refreshedClaim(s)} artifact={{ kind: 'review', thread_id: threadId, section_id: s.id }}
          sx={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', ...(flagged ? { color: 'var(--bad)' } : {}) }}>
          {formatKpiValue(s.value ?? s.claim?.value, s.claim?.format)}
        </ClaimValue>
        <WhyChip s={s} />
      </Box>
      {s.refreshed_at && s.value != null && (
        <Typography variant="caption" sx={{ fontFamily: 'var(--mono)', fontSize: 12, color: s.previous != null ? color : 'var(--ink-3)' }} data-testid="review-tile-delta">
          {deltaText(s.delta, s.previous) || (s.previous != null ? 'no change' : '')} <Box component="span" sx={{ color: 'text.disabled' }}>vs last refresh</Box>
        </Typography>
      )}
      <MiniSpark values={tileSeries(s)} color={color} />
      {flagged && (
        <Box data-testid="review-tile-stale" sx={{ mt: 0.75, justifySelf: 'start', fontSize: 12, fontWeight: 600, color: 'var(--bad)', bgcolor: 'var(--bad-tint)', borderRadius: 999, px: 1, py: 0.1 }}>
          Stale data{stale ? ` · real value ${stale}` : ''}
        </Box>
      )}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {groups.map((g) => (
          <MenuItem key={g} disabled={g === current} data-testid="review-tile-move-to"
            onClick={() => { setAnchor(null); setGroup(threadId, s.id, g === groups[0] ? null : g); }}>
            Move to {g}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
